import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { updateStats } from '@/pages/OilAndGasMap/PortfolioBridge/utils';
import { createStoreSelectors } from '@/stores/createStoreSelectors';
import type { MaAssetEntityTypeEnum } from '@/types/GraphqlSchema';

type Country = {
  id: number;
  isoCode: string;
  name: string;
};

type AssetSelection = {
  id: number;
  legacyId: number;
  country: Country;
  name: string;
  type: MaAssetEntityTypeEnum.Asset;
};

type BlockSelection = {
  id: number;
  legacyId: number;
  name: string;
  country: Country;
  type: MaAssetEntityTypeEnum.Block;
};

type FieldSelection = {
  id: number;
  legacyId: number;
  name: string;
  country: Country;
  type: MaAssetEntityTypeEnum.Field;
};

type EntitySelection = AssetSelection | BlockSelection | FieldSelection;

type ScreeningFilters = {
  countries: Array<{ id: number; isoCode: string; name: string }>;
  companies: Array<{ id: number; name: string }>;
  basins: Array<{ id: number; name: string }>;
  shoreStatuses: Array<{ id: string; name: string }>;
  developmentStatuses: Array<{ id: number; name: string }>;
  npvMin: number | null;
  npvMax: number | null;
  productionMin: number | null;
  productionMax: number | null;
  reservesMin: number | null;
  reservesMax: number | null;
  operator: boolean;
  opportunities: boolean;
};

type Stats = {
  total: number;
  totalAssets: number;
  totalBlocks: number;
  totalFields: number;
};

type PortfolioBridgeState = {
  entities: EntitySelection[];
  filters: ScreeningFilters;
  stats: Stats;
};

const statsInitialValue: Stats = { total: 0, totalAssets: 0, totalBlocks: 0, totalFields: 0 };

const filtersInitialValue: ScreeningFilters = {
  countries: [],
  companies: [],
  basins: [],
  shoreStatuses: [],
  developmentStatuses: [],
  npvMin: null,
  npvMax: null,
  productionMin: null,
  productionMax: null,
  reservesMin: null,
  reservesMax: null,
  operator: false,
  opportunities: false,
};

type PortfolioBridgeActions = {
  addEntities: (entities: EntitySelection[]) => void;
  setFilters: (filters: ScreeningFilters) => void;
  clearSelection: () => void;
};

const portfolioBridgeStoreBase = create(
  devtools<PortfolioBridgeState & PortfolioBridgeActions>(
    set => ({
      entities: [],
      filters: { ...filtersInitialValue },
      stats: { ...statsInitialValue },
      addEntities: (entities: EntitySelection[]) =>
        set(state => ({
          ...state,
          entities,
          stats: updateStats(entities),
        })),
      setFilters: (filters: ScreeningFilters) =>
        set(state => ({
          ...state,
          filters,
        })),
      clearSelection: () =>
        set({
          entities: [],
          filters: { ...filtersInitialValue },
          stats: { ...statsInitialValue },
        }),
    }),
    {
      name: 'portfolioBridge',
    },
  ),
);

const usePortfolioBridgeStore = createStoreSelectors(portfolioBridgeStoreBase);

export { usePortfolioBridgeStore, portfolioBridgeStoreBase };
export type { EntitySelection, AssetSelection, BlockSelection, FieldSelection, ScreeningFilters };
